/**
 * 시스템 소개서 — **슬라이드 한 벌.**
 *
 * 설명서가 "쓰는 법" 이라면 이것은 **"무엇을 하는 물건인가"** 다. 읽는 사람이 다르다 —
 * 도입을 검토하는 고객, 결재하는 사람이고, 대개 콘솔을 한 번도 열어 본 적이 없다.
 *
 * **한 장 = 한 메시지.** 장마다 큰 제목 한 줄, 받쳐 주는 한 줄, 낱말 셋, 콘솔 화면 한 장이다.
 * 고객이 글을 읽지 않고 제목과 그림만 넘겨 봐도 무엇을 하는 물건인지 남아야 한다
 * (2026-10-23 사용자 지시: "고객이 글자를 많이 읽지 않아도 되게"). 분량은 시험이 막는다
 * (`manual-export.test.ts`) — 넘치면 글자를 줄이는 게 아니라 덜어 낸다.
 *
 * 같은 장들이 세 곳에 나간다. 콘솔의 [시스템 소개] 화면(`screens/Overview.tsx`)은 문서처럼
 * 세로로 내려 읽는 슬라이드이고, PDF(`pdf.ts`)는 16:9 쪽, PPT 는 그 쪽을 그대로 슬라이드로
 * 올린 것이다. 원본은 여기 하나다.
 *
 * **적힌 것은 전부 지금 되는 일이다.** 소개서에 앞선 약속을 적으면 그것이 곧 납품 검수의
 * 기준이 된다. 숫자도 출하 검사가 재는 값(편성 반영 45초, 오프라인 판정 90초)만 쓴다.
 * 그림은 설명서와 같은 `shots/` 에 있다(`overview-*` 는 소개서 전용, 관리자 계정·시범 데이터).
 */

/** 기능 한 장 — 왼쪽에 글, 오른쪽에 그 일을 하는 콘솔 화면. */
export interface FeatureSlide {
  kind: 'feature';
  /** 장 머리의 작은 이름. 기능 묶음의 이름이다. */
  label: string;
  /** 이 장이 하는 말 — 고객이 이것만 읽어도 된다. */
  headline: string;
  /** 제목을 받쳐 주는 한 줄. */
  sub: string;
  /** `shots/` 의 그림 id. */
  shot: string;
  /** 낱말 셋 — 문장이 아니라 이름이다. */
  chips: readonly string[];
}

/** 표지 — 제품 이름·약속 한 줄·로그인하면 처음 보는 화면. */
export interface CoverSlide {
  kind: 'cover';
  headline: string;
  sub: string;
  shot: string;
}

/** 큰 숫자 넷. */
export interface NumbersSlide {
  kind: 'numbers';
  label: string;
  headline: string;
  items: readonly { value: string; label: string; note: string }[];
}

/** 그림으로 보여 줄 수 없는 것 — 기기·설치·보안을 이름과 짧은 말로. 옆에 기기 화면 한 장. */
export interface FactsSlide {
  kind: 'facts';
  label: string;
  headline: string;
  shot: string;
  caption: string;
  items: readonly { title: string; text: string }[];
}

/** 도입 순서. */
export interface StepsSlide {
  kind: 'steps';
  label: string;
  headline: string;
  sub: string;
  items: readonly { title: string; text: string }[];
}

/** 맺음 — 약속을 한 번 더, 그리고 함께 받는 것. */
export interface ClosingSlide {
  kind: 'closing';
  headline: string;
  sub: string;
  /** 함께 제공되는 것. 검수 자리에서 "무엇을 받나" 로 이어진다. */
  itemsTitle: string;
  items: readonly string[];
}

export type OverviewSlide = CoverSlide | NumbersSlide | FeatureSlide | FactsSlide | StepsSlide | ClosingSlide;

export const OVERVIEW = {
  product: 'SignageOne',
  tagline: '디지털 사이니지 관리 시스템',
  /** 한 문장 정의 — 문서 정보(PDF subject)와 PPT 설명에 들어간다. */
  lead: '로비·매장·공장의 디스플레이를 한곳에서 편성하고, 현장에 가지 않고 재생 상태까지 확인합니다.',
  supplier: 'MK Solution Co., Ltd.',
  /** 끝 장 바닥의 한 줄. */
  footnote: '이 문서에 적힌 기능은 모두 현재 제품에서 동작합니다.',

  slides: [
    {
      kind: 'cover',
      headline: '모든 화면을, 한곳에서.',
      sub: '무엇을 언제 어느 화면에 재생할지 정하고, 재생했다는 증빙까지 남깁니다.',
      shot: 'overview-dashboard',
    },
    {
      kind: 'numbers',
      label: '한눈에',
      headline: '숫자로 보는 SignageOne',
      /* 출하 검사가 실제로 재는 값만 — 소개서의 숫자는 곧 검수 기준이 된다. */
      items: [
        { value: '45초', label: '편성 반영', note: '바꾼 편성이 화면에 나오기까지' },
        { value: '90초', label: '이상 감지', note: '꺼진 화면이 콘솔에 드러나기까지' },
        { value: '6자리', label: '화면 등록', note: '기기에 뜬 코드만 넣으면 끝' },
        { value: '자동', label: '업데이트', note: '납품 기기는 정한 시간에 스스로 설치' },
      ],
    },
    {
      kind: 'feature',
      label: '콘텐츠',
      headline: '올리면 바로, 어느 화면에서나',
      sub: '이미지·영상·웹 페이지를 올리거나 빈 화면에서 직접 만듭니다.',
      shot: 'overview-content',
      chips: ['영상 자동 변환', '보면서 고치는 HTML', '게시 기간 자동 종료'],
    },
    {
      kind: 'feature',
      label: '템플릿',
      headline: '글만 넣으면 공지가 완성됩니다',
      sub: '회사 로고와 색이 저절로 입혀져, 디자이너 없이 바로 내보냅니다.',
      shot: 'overview-template',
      chips: ['공지·프로모션·긴급알림', '일정·실적 대시보드', '보면서 고치는 미리보기'],
    },
    {
      kind: 'feature',
      label: '편성',
      headline: '언제, 어디에, 무엇을',
      sub: '요일·시간대별 편성을 정하면 45초 안에 화면에 나옵니다.',
      shot: 'overview-schedule',
      chips: ['시간대 스케줄', '기간 캠페인', '긴급 송출'],
    },
    {
      kind: 'feature',
      label: '모니터링',
      headline: '현장에 가지 않고, 지금 화면을',
      sub: '사업장·위치 배치 그대로 화면마다 지금 나오는 모습을 봅니다.',
      shot: 'overview-monitoring',
      chips: ['실시간 화면', '90초 이상 감지', '장애 자동 기록'],
    },
    {
      kind: 'feature',
      label: '원격 관리',
      headline: '멀리서 살피고, 멀리서 고칩니다',
      sub: '기기 상태를 확인하고, 멀리서 재부팅까지 합니다.',
      shot: 'overview-display',
      chips: ['원격 화면 캡처', 'CPU·온도·저장 공간', '원격 재부팅'],
    },
    {
      kind: 'feature',
      label: '재생 증빙',
      headline: '재생했다는 사실이 자료로 남습니다',
      sub: '콘텐츠·화면·일자별 재생 횟수와 노출 시간을 보고서로 받습니다.',
      shot: 'overview-reports',
      chips: ['엑셀·인쇄 보고서', '원자료 CSV', '변경 이력'],
    },
    {
      kind: 'feature',
      label: '여러 현장',
      headline: '흩어진 사업장도 한 지도에서',
      sub: '사업장마다 서버가 따로 있어도 화면 현황을 모아 봅니다.',
      shot: 'sites-map',
      chips: ['통합 모니터링', '인터넷 없이 뜨는 지도', '다른 서버에도 원격 명령'],
    },
    {
      kind: 'facts',
      label: '설치와 기기',
      headline: '가진 기기 그대로, 인터넷이 없어도',
      shot: 'player-pairing',
      caption: '기기 화면에 뜬 6자리 코드를 콘솔에 넣으면 등록이 끝납니다.',
      items: [
        { title: '윈도우 PC', text: '전용 플레이어 설치' },
        { title: '안드로이드 TV 박스', text: '5.1 이상 · 태블릿 포함' },
        { title: '구글 TV 스틱', text: '모니터에 꽂아 바로' },
        { title: '망이 끊겨도 재생', text: '받아 둔 편성을 계속' },
        { title: '폐쇄망 설치', text: 'USB 하나로 서버까지' },
        { title: '보안', text: '권한 4등급·사내 계정 로그인' },
      ],
    },
    {
      kind: 'steps',
      label: '도입',
      headline: '첫 화면까지 다섯 단계',
      sub: '서버 설치는 공급사가 합니다. 그다음은 콘솔에서 바로.',
      items: [
        { title: '기기 준비', text: '플레이어 설치' },
        { title: '화면 등록', text: '6자리 코드 입력' },
        { title: '콘텐츠 올리기', text: '이미지·영상·템플릿' },
        { title: '플레이리스트', text: '순서와 노출 시간' },
        { title: '화면에 배정', text: '45초 안에 재생' },
      ],
    },
    {
      kind: 'closing',
      headline: '무엇을, 언제, 어느 화면에 — 그리고 증빙까지.',
      sub: '현장에 가지 않고 모든 화면을 관리합니다.',
      itemsTitle: '함께 제공되는 것',
      items: ['사용 설명서 (PDF·PPT)', '서버 설치 패키지 (USB)'],
    },
  ] as readonly OverviewSlide[],
} as const;

/** 소개서에 실리는 그림 id 전부 — 시험이 설명서 그림 목록에 있는지 본다. */
export function overviewShots(): string[] {
  return OVERVIEW.slides.flatMap((slide) => ('shot' in slide ? [slide.shot] : []));
}

/** 장 하나가 내놓는 글 전부. */
function slideStrings(slide: OverviewSlide): string[] {
  switch (slide.kind) {
    case 'cover':
      return [slide.headline, slide.sub];
    case 'numbers':
      return [slide.label, slide.headline, ...slide.items.flatMap((item) => [item.value, item.label, item.note])];
    case 'feature':
      return [slide.label, slide.headline, slide.sub, ...slide.chips];
    case 'facts':
      return [slide.label, slide.headline, slide.caption, ...slide.items.flatMap((item) => [item.title, item.text])];
    case 'steps':
      return [slide.label, slide.headline, slide.sub, ...slide.items.flatMap((item) => [item.title, item.text])];
    case 'closing':
      return [slide.headline, slide.sub, slide.itemsTitle, ...slide.items];
  }
}

/** 소개서가 종이에 내놓을 글 전부 — 글꼴 서브셋이 이 글자를 남긴다. */
export function overviewStrings(): string[] {
  return [
    OVERVIEW.product,
    OVERVIEW.tagline,
    OVERVIEW.lead,
    OVERVIEW.supplier,
    OVERVIEW.footnote,
    ...OVERVIEW.slides.flatMap(slideStrings),
  ];
}
